import { MoveRight, Plus, ScrollText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button, Card, CardDescription, CardHeader, CardTitle, Text } from "@athena/ui";
import type { ContactEntry } from "../helpers/client";
import { RECENT_CONTACT_LIMIT } from "../helpers/client";
import { useOpenedFrom } from "../helpers/openedFrom";
import { ContactHistoryList } from "./ContactHistoryList";

interface ContactHistoryCardProps {
    contacts: ContactEntry[];
    /** Opens the parent's log-contact dialog; the parent saves the contact and owns the dialog. */
    onLogContact: () => void;
    /** The full record's contacts tab, where every contact is listed. */
    seeAllTo: string;
}

/**
 * The latest contacts on the profile, as the notes card shows the latest notes: the rest are a link
 * away on the full record, rather than paged in here.
 */
export function ContactHistoryCard({ contacts, onLogContact, seeAllTo }: ContactHistoryCardProps) {
    const { t } = useTranslation();
    // Passed on to the full record, so its way back to the profile keeps the profile's back link.
    const opened = useOpenedFrom();
    const shown = contacts.slice(0, RECENT_CONTACT_LIMIT);

    return (
        <Card width="full">
            <CardHeader className="flex-row items-start gap-3 space-y-0">
                <span className="bg-muted text-muted-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                    <ScrollText className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0 flex-1 space-y-1">
                    <CardTitle>{t("admin.alumni.history.title")}</CardTitle>
                    {contacts.length > 0 ? (
                        <CardDescription>
                            {t("admin.alumni.history.subtitle", { shown: shown.length, total: contacts.length })}
                        </CardDescription>
                    ) : null}
                </div>
                <Button
                    variant="default"
                    size="sm"
                    className="w-auto shrink-0"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={onLogContact}
                >
                    {t("admin.alumni.profile.logContact")}
                </Button>
            </CardHeader>
            <div className="border-border border-t px-5 py-5">
                {contacts.length === 0 ? (
                    <Text size="sm" muted>
                        {t("admin.alumni.history.empty")}
                    </Text>
                ) : (
                    <ContactHistoryList contacts={shown} />
                )}
            </div>
            {/* Outside the padded list wrapper, as on the notes card, so the row sits centered. */}
            {contacts.length > 0 ? (
                <div className="border-border flex items-center justify-center border-t py-4">
                    <Link
                        to={seeAllTo}
                        state={opened}
                        className="text-text-muted hover:text-text-heading flex items-center gap-2 text-sm"
                    >
                        {t("admin.alumni.history.seeAll", { count: contacts.length })}
                        <MoveRight className="h-4 w-4" aria-hidden />
                    </Link>
                </div>
            ) : null}
        </Card>
    );
}
